'use client';
import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import TopNav from '../components/TopNav';
import MarketTicker from '../components/MarketTicker';
import BreakingNewsBar from '../components/BreakingNewsBar';
import FooterBar from '../components/FooterBar';

interface CoinQuote {
  id: number;
  name: string;
  symbol: string;
  price: number;
  change24h: number;
  marketCap: number;
  volume24h: number;
}

interface GlobalMetrics {
  totalMarketCap: number;
  totalVolume24h: number;
  btcDominance: number;
  activeCryptocurrencies: number;
}

const CRYPTO_SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA'];

const globalMarkets = [
  { id: 'm-1', name: 'S&P 500', value: '5,234.18', change: '+0.48%', positive: true, region: 'US' },
  { id: 'm-2', name: 'NASDAQ', value: '16,428.82', change: '+0.72%', positive: true, region: 'US' },
  { id: 'm-3', name: 'Dow Jones', value: '38,892.80', change: '+0.21%', positive: true, region: 'US' },
  { id: 'm-4', name: 'FTSE 100', value: '7,682.50', change: '-0.14%', positive: false, region: 'UK' },
  { id: 'm-5', name: 'DAX', value: '17,842.30', change: '+0.33%', positive: true, region: 'DE' },
  { id: 'm-6', name: 'Nikkei 225', value: '38,460.08', change: '-0.62%', positive: false, region: 'JP' },
];

const commodities = [
  { id: 'co-1', name: 'Gold', price: '$2,342.80', change: '+0.34%', positive: true, unit: 'oz' },
  { id: 'co-2', name: 'Silver', price: '$27.84', change: '+0.18%', positive: true, unit: 'oz' },
  { id: 'co-3', name: 'Crude Oil (WTI)', price: '$82.14', change: '-0.56%', positive: false, unit: 'bbl' },
  { id: 'co-4', name: 'Natural Gas', price: '$2.18', change: '-1.24%', positive: false, unit: 'MMBtu' },
];

function formatPrice(price: number): string {
  if (price >= 1000) return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (price >= 1) return '$' + price.toFixed(2);
  if (price >= 0.01) return '$' + price.toFixed(4);
  return '$' + price.toFixed(8);
}

function formatChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

function formatLargeNum(n: number): string {
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  return '$' + n.toFixed(0);
}

export default function MarketsPage() {
  const [coins, setCoins] = useState<CoinQuote[]>([]);
  const [globalMetrics, setGlobalMetrics] = useState<GlobalMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch('/api/crypto/market-data');
        if (!res.ok) return;
        const json = await res.json();
        setCoins(json.coins ?? []);
        setGlobalMetrics(json.globalMetrics ?? null);
      } catch {
        // keep static fallback
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const cryptoMarkets = CRYPTO_SYMBOLS.map((sym) => {
    const coin = coins.find((c) => c.symbol === sym);
    if (!coin) return null;
    return {
      id: `cm-${sym.toLowerCase()}`,
      pair: `${sym}/USD`,
      price: formatPrice(coin.price),
      change: formatChange(coin.change24h),
      positive: coin.change24h >= 0,
      high: '—',
      low: '—',
      vol: formatLargeNum(coin.volume24h),
    };
  }).filter(Boolean) as NonNullable<ReturnType<typeof Array.prototype.map>[number]>[];

  const summaryStats = [
    {
      label: 'Crypto Market Cap',
      value: globalMetrics ? formatLargeNum(globalMetrics.totalMarketCap) : '$2.41T',
      change: '+1.8%',
      positive: true,
    },
    {
      label: 'Total 24H Volume',
      value: globalMetrics ? formatLargeNum(globalMetrics.totalVolume24h) : '$89.2B',
      change: '+5.2%',
      positive: true,
    },
    {
      label: 'BTC Dominance',
      value: globalMetrics ? globalMetrics.btcDominance.toFixed(1) + '%' : '55.6%',
      change: '-0.3%',
      positive: false,
    },
    { label: 'Fear & Greed', value: '72 — Greed', change: 'Bullish', positive: true },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <MarketTicker />
      <BreakingNewsBar />
      <main className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-6">
        <div className="mb-6">
          <span className="text-crypto-green text-xs font-bold uppercase tracking-widest">Live Data</span>
          <h1 className="text-3xl font-black text-foreground mt-1">Global Markets</h1>
          <p className="text-muted-foreground text-sm mt-1">Real-time market data across crypto, equities, and commodities.</p>
        </div>

        {/* Market Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {summaryStats?.map((stat) => (
            <div key={stat?.label} className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{stat?.label}</p>
              <p className="text-lg font-black text-foreground tabular-nums">{stat?.value}</p>
              <p className={`text-xs font-semibold mt-0.5 ${stat?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>{stat?.change}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Crypto Pairs */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="text-base font-semibold text-foreground">Crypto Pairs</h2>
              <Link href="/crypto" className="text-xs text-crypto-green hover:text-crypto-green/80 font-medium flex items-center gap-1">View All <ArrowRight size={12} /></Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2.5 text-xs text-muted-foreground font-medium">Pair</th>
                    <th className="text-right px-4 py-2.5 text-xs text-muted-foreground font-medium">Price</th>
                    <th className="text-right px-4 py-2.5 text-xs text-muted-foreground font-medium">24H %</th>
                    <th className="text-right px-4 py-2.5 text-xs text-muted-foreground font-medium hidden sm:table-cell">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-muted-foreground">Loading live data…</td></tr>
                  ) : cryptoMarkets.length > 0 ? cryptoMarkets?.map((m) => (
                    <tr key={m?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors duration-150">
                      <td className="px-4 py-2.5 text-sm font-semibold text-foreground font-mono">{m?.pair}</td>
                      <td className="px-4 py-2.5 text-right text-sm font-semibold text-foreground tabular-nums font-mono">{m?.price}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`flex items-center justify-end gap-0.5 text-sm font-semibold ${m?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>
                          {m?.positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {m?.change}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs text-muted-foreground hidden sm:table-cell tabular-nums">{m?.vol}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-muted-foreground">No data available</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Global Indices + Commodities */}
          <div className="flex flex-col gap-4">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">Global Indices</h2>
              </div>
              <div className="divide-y divide-border/50">
                {globalMarkets?.map((m) => (
                  <div key={m?.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors duration-150">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-mono">{m?.region}</span>
                      <span className="text-sm font-medium text-foreground">{m?.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-foreground tabular-nums font-mono">{m?.value}</span>
                      <span className={`flex items-center gap-0.5 text-xs font-semibold ${m?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>
                        {m?.positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {m?.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">Commodities</h2>
              </div>
              <div className="divide-y divide-border/50">
                {commodities?.map((c) => (
                  <div key={c?.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors duration-150">
                    <div>
                      <span className="text-sm font-medium text-foreground">{c?.name}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">/ {c?.unit}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-foreground tabular-nums font-mono">{c?.price}</span>
                      <span className={`flex items-center gap-0.5 text-xs font-semibold ${c?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>
                        {c?.positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {c?.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}
